import type { Survey, SurveySession } from '../../../types';
import type { ApiQuestion, ApiResponse } from '../../../internalTypes';
import { decodeInstanceJwt } from '../../../../utils/decodeInstanceJwt';
import { extractSurveyLanguages } from '../../extractSurveyLanguages';
import { mapSurveyPages } from '../../mapSurveyPages';
import { mapPhaseFlags, mapSpecialPages } from './mapSpecialPages';
import { mapCtaButton } from './mapCtaButton';

export function extractQuestions(api: ApiResponse): ApiQuestion[] {
  const surveyPages = api.cfmSurveyDTO?.surveyPages ?? [];
  return surveyPages.flatMap(
    page => page.questionHolders?.map(holder => holder.question).filter((q): q is ApiQuestion => Boolean(q)) ?? []
  );
}

export function extractSession(api: ApiResponse): SurveySession | null {
  if (!api.instanceId?.trim()) return null;

  const instanceId = api.instanceId.trim();
  const jwtPayload = decodeInstanceJwt(instanceId);

  return {
    instanceId,
    language: api.surveyLanguage ?? api.cfmSurveyDTO?.baseLanguage ?? 'en',
    responseId: jwtPayload?.surveyResponseId,
    ...(api.instanceCreatedTime !== undefined
      ? { instanceCreatedTime: api.instanceCreatedTime }
      : {}),
  };
}

export function resolveSurveyLanguage(api: ApiResponse, language?: string): string {
  return language ?? api.surveyLanguage ?? api.cfmSurveyDTO?.baseLanguage ?? 'en';
}

export function mapSurvey(api: ApiResponse, language?: string, placeholders?: Record<string, string>): Survey {
  const surveyLanguage = resolveSurveyLanguage(api, language);
  const languages = extractSurveyLanguages(api);
  const pages = mapSurveyPages(api, surveyLanguage, placeholders);
  const {
    introPage,
    endPages,
    pausedPage,
    submittedPage,
    expiredPage,
    quotaFulfilledPage,
  } = mapSpecialPages(api, surveyLanguage);
  const phaseFlags = mapPhaseFlags(api);
  const submitCta = mapCtaButton(api.cfmSurveyDTO?.ctaConfig?.submit, surveyLanguage, {
    defaultLabel: 'Submit',
  });

  return {
    id: api.cfmSurveyDTO?.id ?? '',
    language: surveyLanguage,
    languages,
    pages,
    ...(submitCta ? { cta: { submit: submitCta } } : {}),
    ...(introPage ? { introPage } : {}),
    endPages,
    ...(pausedPage ? { pausedPage } : {}),
    ...(submittedPage ? { submittedPage } : {}),
    ...(expiredPage ? { expiredPage } : {}),
    ...(quotaFulfilledPage ? { quotaFulfilledPage } : {}),
    phaseFlags,
  };
}
